import React, { useState } from 'react';
import {
  TextField,
  IconButton,
  Popover,
  Typography,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Autocomplete
} from '@mui/material';
import { toDateInputValue } from '../utils/filterUtils';

export interface FilterConfig {
  field: string;
  type: 'string' | 'number' | 'date' | 'select';
  label: string;
  options?: { value: string; label: string }[];
  placeholder?: string;
  autocomplete?: boolean;
  range?: boolean;
  min?: string | number | null;
  max?: string | number | null;
}

interface ColumnFilterProps {
  config: FilterConfig;
  value: any;
  onChange: (value: any) => void;
  onClear: () => void;
}

const ColumnFilter: React.FC<ColumnFilterProps> = ({ config, value, onChange, onClear }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [tempValue, setTempValue] = useState(value);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setTempValue(value);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    onChange(tempValue);
    handleClose();
  };

  const handleClear = () => {
    setTempValue('');
    onChange('');
    onClear();
    handleClose();
  };

  const isFilterActive = value && (
    typeof value === 'object'
      ? Object.values(value).some((entry) => entry != null && entry !== '')
      : value !== ''
  );

  const useAutocomplete = config.autocomplete || (config.options && config.options.length > 8);
  const dateMin = toDateInputValue(config.min);
  const dateMax = toDateInputValue(config.max);

  const renderFilterInput = () => {
    switch (config.type) {
      case 'string':
        return (
          <TextField
            size="small"
            label={config.label}
            value={tempValue || ''}
            onChange={(e) => setTempValue(e.target.value)}
            placeholder={config.placeholder || `Filter by ${config.label}`}
            fullWidth
            variant="outlined"
          />
        );

      case 'number':
        if (config.range) {
          return (
            <Stack spacing={2}>
              <TextField
                size="small"
                label="Min"
                type="number"
                value={tempValue?.min ?? ''}
                onChange={(e) => setTempValue({ ...(tempValue || {}), min: e.target.value })}
                placeholder={config.min != null ? String(config.min) : 'Min'}
                inputProps={{ min: config.min ?? undefined, max: config.max ?? undefined }}
                fullWidth
                variant="outlined"
              />
              <TextField
                size="small"
                label="Max"
                type="number"
                value={tempValue?.max ?? ''}
                onChange={(e) => setTempValue({ ...(tempValue || {}), max: e.target.value })}
                placeholder={config.max != null ? String(config.max) : 'Max'}
                inputProps={{ min: config.min ?? undefined, max: config.max ?? undefined }}
                fullWidth
                variant="outlined"
              />
            </Stack>
          );
        }
        return (
          <TextField
            size="small"
            label={config.label}
            type="number"
            value={tempValue || ''}
            onChange={(e) => setTempValue(e.target.value)}
            placeholder={config.placeholder || `Filter by ${config.label}`}
            fullWidth
            variant="outlined"
          />
        );

      case 'date':
        return (
          <Stack spacing={2}>
            <TextField
              size="small"
              label="From Date"
              type="date"
              value={tempValue?.start || ''}
              onChange={(e) => setTempValue({ ...tempValue, start: e.target.value })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: dateMin || undefined, max: dateMax || undefined }}
              fullWidth
              variant="outlined"
            />
            <TextField
              size="small"
              label="To Date"
              type="date"
              value={tempValue?.end || ''}
              onChange={(e) => setTempValue({ ...tempValue, end: e.target.value })}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: dateMin || undefined, max: dateMax || undefined }}
              fullWidth
              variant="outlined"
            />
          </Stack>
        );

      case 'select':
        if (useAutocomplete) {
          const selected = config.options?.find((option) => option.value === (tempValue || '')) || null;
          return (
            <Autocomplete
              size="small"
              options={config.options || []}
              getOptionLabel={(option) => option.label}
              value={selected}
              onChange={(_event, newValue) => setTempValue(newValue ? newValue.value : '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={config.label}
                  placeholder={config.placeholder || `Filter by ${config.label}`}
                />
              )}
            />
          );
        }
        return (
          <FormControl fullWidth size="small">
            <InputLabel>{config.label}</InputLabel>
            <Select
              value={tempValue || ''}
              onChange={(e: SelectChangeEvent) => setTempValue(e.target.value)}
              label={config.label}
            >
              <MenuItem value="">All {config.label}s</MenuItem>
              {config.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        color={isFilterActive ? 'primary' : 'default'}
        sx={{ 
          ml: 1,
          opacity: isFilterActive ? 1 : 0.6,
          '&:hover': { opacity: 1 }
        }}
      >
        <span style={{ fontSize: '16px' }}>🔍</span>
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: { p: 2, minWidth: 200 }
        }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Filter by {config.label}
        </Typography>
        
        {renderFilterInput()}
        
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button size="small" onClick={handleApply} variant="contained">
            Apply
          </Button>
          <Button size="small" onClick={handleClear} variant="outlined">
            Clear
          </Button>
        </Stack>
      </Popover>
    </>
  );
};

export default ColumnFilter;
